// Modelo303Page.tsx — standalone indexable "Calculadora Modelo 303" page (F15b, plan §4 F15).
//
// Lives OUTSIDE the SPA shell (no Firebase/Sentry/contexts) — see the F15a pattern notes in
// CalculadoraIrpf2025Page.tsx. Every figure comes from computeModelo303Estimate, the same
// VAT-extraction arithmetic as the in-app estimator, with rates from IVA_RATES_2025.

/* eslint-disable gestorpro/no-spanish-jsx --
   Documented F15a/F15b decision (plan §4): standalone Spanish-market SEO page rendered
   OUTSIDE the app shell (no i18n runtime); copy is direct Spanish by design. */

import React, { useMemo, useState } from 'react';
import { ID_DICTIONARY } from '../../constants/id-dictionary';
import { CalculatorShell } from '../shared/CalculatorShell';
import { computeModelo303Estimate, MODELO_303_RATE_OPTIONS } from './calc';

const AMOUNT_MIN = 0;
const AMOUNT_MAX = 10000000;

const formatEuros = (value: number): string =>
    `${value.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const clampAmount = (value: number): number =>
    Number.isFinite(value) ? Math.min(AMOUNT_MAX, Math.max(AMOUNT_MIN, Math.round(value))) : 0;

export const Modelo303Page: React.FC = () => {
    const [income, setIncome] = useState(15000);
    const [incomeRate, setIncomeRate] = useState(21);
    const [expenses, setExpenses] = useState(5000);
    const [expenseRate, setExpenseRate] = useState(21);

    const estimate = useMemo(
        () =>
            computeModelo303Estimate({
                grossIncomeWithVat: income,
                incomeRatePct: incomeRate,
                deductibleExpensesWithVat: expenses,
                expenseRatePct: expenseRate,
            }),
        [income, incomeRate, expenses, expenseRate]
    );

    return (
        <CalculatorShell
            rootId={ID_DICTIONARY.CALCULATORS.M303_ROOT}
            heading="Calculadora Modelo 303 (2025)"
            intro="Estima el IVA trimestral a ingresar (IVA repercutido menos IVA soportado deducible) con la misma lógica que usa GestorPro. Gratis, sin registro y calculado íntegramente en tu navegador."
            disclaimerId={ID_DICTIONARY.CALCULATORS.M303_DISCLAIMER}
            disclaimerBody="Estimación orientativa y simplificada sobre los datos que introduces: el modelo 303 oficial se autoliquida con tus facturas reales, casilla a casilla, y el importe final puede diferir. No constituye asesoramiento fiscal."
            ctaId={ID_DICTIONARY.CALCULATORS.M303_CTA_APP}
            ctaHeading="Deja de calcularlo a mano"
            ctaBody="GestorPro importa tus extractos bancarios y estima tus modelos 303 y 130 automáticamente a partir de tus movimientos reales, con el IVA de cada factura o el tipo por categoría."
            currentPath="/calculadoras/modelo-303/"
            scopeNote="Cálculo según la normativa estatal (AEAT) con los tipos de IVA de 2025. Si tributas en País Vasco o Navarra (régimen foral), tu hacienda foral tiene modelos propios. Si tu IVA soportado supera al repercutido, esta estimación muestra 0 € a ingresar."
        >
            {/* Form card */}
            <section className="bg-card border border-border rounded-xl shadow-e1 p-5 sm:p-6 flex flex-col gap-5">
                <div className="flex items-center gap-2 border-b border-border pb-3">
                    <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                    <h2 className="text-xs font-extrabold text-muted-foreground uppercase tracking-wider">
                        Tus datos del trimestre
                    </h2>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                        <label htmlFor={ID_DICTIONARY.CALCULATORS.M303_INCOME_INPUT} className="text-xs font-bold text-muted-foreground">
                            Ingresos del trimestre (IVA incluido)
                        </label>
                        <div className="flex items-center gap-1">
                            <input
                                id={ID_DICTIONARY.CALCULATORS.M303_INCOME_INPUT}
                                type="number"
                                inputMode="numeric"
                                min={AMOUNT_MIN}
                                max={AMOUNT_MAX}
                                step={100}
                                value={income}
                                onChange={(e) => setIncome(clampAmount(Number(e.target.value)))}
                                className="w-32 bg-muted border-2 border-border rounded-xl px-2.5 py-1.5 text-sm font-extrabold text-foreground text-right tabular-nums focus:outline-none focus:border-primary transition-colors"
                            />
                            <span className="text-sm font-extrabold text-foreground">€</span>
                        </div>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                        <label htmlFor={ID_DICTIONARY.CALCULATORS.M303_INCOME_RATE_SELECT} className="text-xs font-bold text-muted-foreground">
                            Tipo de IVA repercutido
                        </label>
                        <select
                            id={ID_DICTIONARY.CALCULATORS.M303_INCOME_RATE_SELECT}
                            value={incomeRate}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setIncomeRate(Number(e.target.value))}
                            className="bg-muted border-2 border-border rounded-xl px-3 py-2 text-sm font-bold text-foreground focus:outline-none focus:border-primary transition-colors cursor-pointer"
                        >
                            {MODELO_303_RATE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3">
                        <label htmlFor={ID_DICTIONARY.CALCULATORS.M303_EXPENSES_INPUT} className="text-xs font-bold text-muted-foreground">
                            Gastos deducibles del trimestre (IVA incluido)
                        </label>
                        <div className="flex items-center gap-1">
                            <input
                                id={ID_DICTIONARY.CALCULATORS.M303_EXPENSES_INPUT}
                                type="number"
                                inputMode="numeric"
                                min={AMOUNT_MIN}
                                max={AMOUNT_MAX}
                                step={100}
                                value={expenses}
                                onChange={(e) => setExpenses(clampAmount(Number(e.target.value)))}
                                className="w-32 bg-muted border-2 border-border rounded-xl px-2.5 py-1.5 text-sm font-extrabold text-foreground text-right tabular-nums focus:outline-none focus:border-primary transition-colors"
                            />
                            <span className="text-sm font-extrabold text-foreground">€</span>
                        </div>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                        <label htmlFor={ID_DICTIONARY.CALCULATORS.M303_EXPENSE_RATE_SELECT} className="text-xs font-bold text-muted-foreground">
                            Tipo de IVA soportado
                        </label>
                        <select
                            id={ID_DICTIONARY.CALCULATORS.M303_EXPENSE_RATE_SELECT}
                            value={expenseRate}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setExpenseRate(Number(e.target.value))}
                            className="bg-muted border-2 border-border rounded-xl px-3 py-2 text-sm font-bold text-foreground focus:outline-none focus:border-primary transition-colors cursor-pointer"
                        >
                            {MODELO_303_RATE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <p className="text-[11px] text-muted-foreground leading-relaxed bg-muted/40 border border-border p-3 rounded-xl">
                    Supuestos del cálculo: importes con IVA incluido y un único tipo medio por bloque
                    (ventas y compras). En la app, cada movimiento usa el IVA real de su factura o el
                    tipo por defecto de su categoría.
                </p>
            </section>

            {/* Result card */}
            <section
                id={ID_DICTIONARY.CALCULATORS.M303_RESULT_CARD}
                className="bg-card border border-border rounded-xl shadow-e1 p-5 sm:p-6 flex flex-col gap-4"
                aria-live="polite"
            >
                <h2 className="text-xs font-extrabold text-muted-foreground uppercase tracking-wider">
                    Resultado estimado
                </h2>
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <span className="type-label block">IVA estimado a ingresar</span>
                        <span className="type-kpi-hero text-foreground">{formatEuros(estimate.result)}</span>
                    </div>
                    <div className="text-right space-y-1">
                        <div>
                            <span className="type-label block">IVA repercutido</span>
                            <span className="type-kpi-sm text-foreground">{formatEuros(estimate.ivaRepercutido)}</span>
                        </div>
                        <div>
                            <span className="type-label block">IVA soportado</span>
                            <span className="type-kpi-sm text-foreground">{formatEuros(estimate.ivaSoportado)}</span>
                        </div>
                    </div>
                </div>
                <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                    <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                    El cálculo se ejecuta en tu navegador: tus cifras no se envían a ningún servidor.
                </p>
            </section>
        </CalculatorShell>
    );
};
